import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/lib/ui/Dialog';
import { Button } from '@/lib/ui/Button';
import { ConfirmDialog } from '@/lib/ui/ConfirmDialog';
import type { MintedApiToken } from '@/lib/api/types';

/**
 * The one-time secret.
 *
 * This panel exists because of one fact: the secret is never readable again. It
 * is sha256'd at rest, and no endpoint returns it. If the operator closes this
 * without copying it, the token is dead and has to be re-minted — so the design
 * job is to make "you must copy this now" impossible to miss and impossible to
 * do accidentally.
 *
 * Three decisions follow from that:
 *  - closing is guarded by a confirm, and the confirm is skipped once something
 *    has actually been copied (the warning stops nagging once it is moot);
 *  - the secret and the ready-to-paste command are both one click away, because
 *    the command is what the operator actually needs and retyping a 43-character
 *    secret by hand is how people end up with a broken connector;
 *  - the connector setup is shown here, at the only moment the secret exists,
 *    rather than in a doc they would have to find later.
 */
export function SecretPanel({
  minted,
  apiBaseUrl,
  onClose,
}: {
  minted: MintedApiToken;
  /** The absolute API base, from the server. See ApiTokenOptions.api_base_url. */
  apiBaseUrl: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<'secret' | 'command' | null>(null);
  const [confirmingClose, setConfirmingClose] = useState(false);
  const hasCopied = useRef(false);

  // The copy confirmation is transient; clear it so a second copy still reads
  // as a fresh acknowledgement rather than a stuck label.
  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(null), 2200);
    return () => clearTimeout(timer);
  }, [copied]);

  const command = useMemo(
    () => buildCommand(minted.secret, apiBaseUrl),
    [minted.secret, apiBaseUrl],
  );

  async function copy(value: string, which: 'secret' | 'command') {
    try {
      await navigator.clipboard.writeText(value);
      hasCopied.current = true;
      setCopied(which);
    } catch {
      // Clipboard access can be denied (insecure origin, permissions). Select the
      // text instead so ⌘C still works — never leave the operator stranded with
      // a credential they cannot retrieve.
      setCopied(null);
      const node = document.getElementById(which === 'secret' ? 'token-secret' : 'token-command');
      if (node) {
        const range = document.createRange();
        range.selectNodeContents(node);
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }
    }
  }

  function requestClose() {
    if (hasCopied.current) {
      onClose();
      return;
    }
    setConfirmingClose(true);
  }

  return (
    <>
      <Dialog
        open
        onOpenChange={(open) => !open && requestClose()}
        title={t('apiTokens.secret.title')}
        description={t('apiTokens.secret.description', { name: minted.token.name })}
        size="lg"
        footer={
          <Button onClick={requestClose}>{t('apiTokens.secret.done')}</Button>
        }
      >
        <div className="flex flex-col gap-lg">
          <p
            role="alert"
            className="rounded-sm border-[0.5px] border-signal-amber/50 bg-signal-amber-fill px-md py-sm text-small text-signal-amber-ink"
          >
            {t('apiTokens.secret.warning')}
          </p>

          <CopyBlock
            id="token-secret"
            label={t('apiTokens.secret.secretLabel')}
            copyLabel={t('apiTokens.secret.copySecret')}
            value={minted.secret}
            copied={copied === 'secret'}
            onCopy={() => void copy(minted.secret, 'secret')}
          />

          <div className="flex flex-col gap-sm border-t-[0.5px] border-hairline pt-lg">
            <p className="eyebrow text-ink-mute">{t('apiTokens.secret.setupLabel')}</p>
            <p className="max-w-[70ch] text-small text-ink-mid">{t('apiTokens.secret.setupHelp')}</p>
            <CopyBlock
              id="token-command"
              copyLabel={t('apiTokens.secret.copyCommand')}
              value={command}
              copied={copied === 'command'}
              onCopy={() => void copy(command, 'command')}
              multiline
            />
            <p className="text-small text-ink-mute">{t('apiTokens.secret.gitWarning')}</p>
          </div>

          <dl className="grid grid-cols-2 gap-md border-t-[0.5px] border-hairline pt-lg text-small">
            <Fact label={t('apiTokens.columns.account')} value={`${minted.token.account_name} · ${minted.token.account_role}`} />
            <Fact
              label={t('apiTokens.columns.grants')}
              value={minted.token.effective_permissions.join(', ')}
            />
            <Fact
              label={t('apiTokens.columns.collections')}
              value={
                minted.token.collections.length > 0
                  ? minted.token.collections.join(', ')
                  : t('apiTokens.allCollections')
              }
            />
            <Fact
              label={t('apiTokens.columns.expires')}
              value={
                minted.token.expires_at
                  ? new Date(minted.token.expires_at).toLocaleDateString()
                  : t('apiTokens.neverExpires')
              }
            />
          </dl>
        </div>
      </Dialog>

      <ConfirmDialog
        open={confirmingClose}
        onOpenChange={setConfirmingClose}
        title={t('apiTokens.secret.confirmClose.title')}
        description={t('apiTokens.secret.confirmClose.description')}
        confirmLabel={t('apiTokens.secret.confirmClose.confirm')}
        tone="danger"
        onConfirm={() => {
          setConfirmingClose(false);
          onClose();
        }}
      />
    </>
  );
}

function CopyBlock({
  id,
  label,
  copyLabel,
  value,
  copied,
  onCopy,
  multiline,
}: {
  id: string;
  label?: string;
  /** The button's accessible name. "Copy" alone is ambiguous with two of them. */
  copyLabel: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
  multiline?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-[6px]">
      {label && <span className="eyebrow text-ink-mute">{label}</span>}
      <div className="flex items-stretch gap-xs">
        <code
          id={id}
          // `dir="ltr"` and `text-start` keep the credential and the shell command
          // readable in the Arabic layout: they are code, not prose, and mirroring
          // them would make the value unreadable and unsafe to copy by eye.
          dir="ltr"
          className={
            'flex-1 select-all overflow-x-auto rounded-sm border-[0.5px] border-hairline bg-canvas-sand px-md py-sm text-start font-mono text-small text-ink-graphite ' +
            (multiline ? 'whitespace-pre-wrap break-all' : 'whitespace-nowrap')
          }
        >
          {value}
        </code>
        <Button variant="ghost" onClick={onCopy} aria-label={copyLabel} aria-live="polite">
          {copied ? t('apiTokens.secret.copied') : t('common.copy')}
        </Button>
      </div>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-[2px]">
      <dt className="eyebrow text-ink-mute">{label}</dt>
      <dd className="text-ink-mid">{value}</dd>
    </div>
  );
}

/**
 * The ready-to-paste registration command.
 *
 * `apiBaseUrl` is the SERVER's own answer to "where is this API", not something
 * derived from the console's origin. The admin SPA and the API are different
 * hosts in every real deployment, so guessing here would emit a command that
 * looks right and fails later as an unexplained 401.
 */
function buildCommand(secret: string, apiBaseUrl: string): string {
  // An empty value would emit `PHARADEV_CMS_API_URL=` — a command that pastes
  // cleanly and then fails with an error pointing nowhere near the cause. An
  // obvious placeholder makes the gap impossible to paste past.
  const apiUrl = apiBaseUrl.replace(/\/+$/, '') || '<SET_THE_CMS_API_URL>';
  return [
    'claude mcp add pharadev-cms \\',
    `  --env PHARADEV_CMS_API_URL=${apiUrl} \\`,
    `  --env PHARADEV_CMS_TOKEN=${secret} \\`,
    '  --env PHARADEV_MEDIA_ROOT=/path/to/your/assets \\',
    `  --env PHARADEV_ADMIN_URL=${window.location.origin} \\`,
    '  -- node /path/to/pharadev-mcp/dist/index.js',
  ].join('\n');
}
